import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import {
  AdDataAnalysisResponse,
  AdTypeSummary,
  TouchpointDetail,
  DailyAdDataItem,
  UserBehaviorSummary,
  BannerPositionStats,
  TouchpointDailyMatrixResponse,
} from '../../types/Ad.api.type';
import {
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

const PERIOD_OPTIONS = [
  { value: 'YESTERDAY', label: '어제' },
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
];

export const AdDataAnalysisDashboard = () => {
  const [period, setPeriod] = useState('YESTERDAY');
  const [customDate, setCustomDate] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const twoWeeksAgo = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
  const [matrixFrom, setMatrixFrom] = useState(twoWeeksAgo);
  const [matrixTo, setMatrixTo] = useState(today);

  const queryPeriod = customDate ? `DATE:${customDate}` : period;

  const { data, isLoading } = useQuery({
    queryKey: ['adminAdDataAnalysis', queryPeriod],
    queryFn: () => adApi.getAdDataAnalysis(queryPeriod).then(res => res.data as AdDataAnalysisResponse),
  });

  const { data: matrixData } = useQuery({
    queryKey: ['touchpointDailyMatrix', matrixFrom, matrixTo],
    queryFn: () => adApi.getTouchpointDailyMatrix(matrixFrom, matrixTo).then(res => res.data as TouchpointDailyMatrixResponse),
  });

  const handlePeriodClick = (value: string) => {
    setPeriod(value);
    setCustomDate('');
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDate(e.target.value);
    setPeriod('');
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  if (!data) {
    return <div className="text-center py-8 text-gray-500">데이터가 없습니다.</div>;
  }

  return (
    <div className="space-y-6">
      {/* 기간 필터 + 날짜 선택 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-700">
          분석 기간: <span className="font-mono">{data.periodLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                variant={period === opt.value && !customDate ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7 px-2"
                onClick={() => handlePeriodClick(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <input
            type="date"
            value={customDate}
            onChange={handleDateChange}
            className="h-7 px-2 text-xs border rounded bg-white"
          />
        </div>
      </div>

      {/* 광고 타입별 요약 */}
      <AdTypeSummaryTable rewardAd={data.rewardAd} bannerAd={data.bannerAd} />

      {/* 터치포인트별 상세 */}
      {data.touchpoints.length > 0 && <TouchpointTable items={data.touchpoints} />}

      {/* 일별 데이터 (핵심 테이블) */}
      {data.dailyData.length > 0 && <DailyDataTable items={data.dailyData} />}

      {/* 사용자 행동 요약 */}
      <UserBehaviorCard data={data.userBehavior} />

      {/* 배너 위치별 성과 */}
      {data.bannerPositions.length > 0 && <BannerPositionTable items={data.bannerPositions} />}

      {/* 터치포인트별 일별 매트릭스 */}
      {matrixData && <TouchpointDailyMatrixSection data={matrixData} matrixFrom={matrixFrom} matrixTo={matrixTo} setMatrixFrom={setMatrixFrom} setMatrixTo={setMatrixTo} />}
    </div>
  );
};

// ==================== 광고 타입별 요약 ====================

const AdTypeSummaryTable = ({ rewardAd, bannerAd }: { rewardAd: AdTypeSummary; bannerAd: AdTypeSummary }) => (
  <Card>
    <div className="p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">광고 타입별 요약</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-3">타입</th>
              <th className="pb-2 text-right">노출/시청</th>
            </tr>
          </thead>
          <tbody>
            {[rewardAd, bannerAd].map(item => (
              <tr key={item.type} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 pr-3 font-medium">{item.type}</td>
                <td className="py-2 text-right font-mono">{item.totalCount.toLocaleString()}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-medium">
              <td className="py-2 pr-3">합계</td>
              <td className="py-2 text-right font-mono">{(rewardAd.totalCount + bannerAd.totalCount).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

// ==================== 터치포인트별 상세 ====================

const TOUCHPOINT_GROUPS = [
  { key: 'vote', label: '투표 계열', locations: ['VOTE_AD_BONUS', 'DAILY_AD_BONUS', 'MILESTONE_AD_BONUS', 'CREATOR_MILESTONE_AD_BONUS'] },
  { key: 'attendance', label: '출석 계열', locations: ['ATTENDANCE_AD_BONUS', 'ATTENDANCE_STREAK_SAVE'] },
  { key: 'activity', label: '활동 보상', locations: ['COMMENT_AD_BONUS', 'SURVEY_LIKE_AD_BONUS', 'COMMENT_LIKE_AD_BONUS', 'WEEKLY_MISSION_AD_BONUS'] },
  { key: 'etc', label: '기타', locations: [] as string[] },
];

const groupTouchpointItems = (items: TouchpointDetail[]) => {
  const assignedLocations = new Set(TOUCHPOINT_GROUPS.flatMap(g => g.locations));
  return TOUCHPOINT_GROUPS.map(group => {
    const groupItems = group.key === 'etc'
      ? items.filter(item => !assignedLocations.has(item.location))
      : items.filter(item => group.locations.includes(item.location));
    const subtotal = groupItems.reduce((sum, i) => sum + i.watchCount, 0);
    return { ...group, items: groupItems, subtotal };
  }).filter(g => g.items.length > 0);
};

const TouchpointTable = ({ items }: { items: TouchpointDetail[] }) => {
  const total = items.reduce((sum, i) => sum + i.watchCount, 0);
  const groups = groupTouchpointItems(items);

  return (
    <Card>
      <div className="p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">터치포인트별 리워드 광고 시청</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-3">터치포인트</th>
                <th className="pb-2 pr-3 text-right">시청 횟수</th>
                <th className="pb-2 text-right">비율</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, gi) => (
                <React.Fragment key={group.key}>
                  {/* 그룹 헤더 */}
                  <tr className={`bg-gray-100 ${gi > 0 ? 'border-t-2' : ''}`}>
                    <td colSpan={3} className="py-1.5 px-3 text-xs font-semibold text-gray-600 uppercase">{group.label}</td>
                  </tr>
                  {/* 그룹 아이템 */}
                  {group.items.map(item => (
                    <tr key={item.location} className="border-b hover:bg-gray-50">
                      <td className="py-2 pr-3 pl-3">{item.label}</td>
                      <td className="py-2 pr-3 text-right font-mono">{item.watchCount.toLocaleString()}</td>
                      <td className="py-2 text-right font-mono">
                        {total > 0 ? (item.watchCount / total * 100).toFixed(1) : '0.0'}%
                      </td>
                    </tr>
                  ))}
                  {/* 그룹 소계 */}
                  <tr className="bg-gray-50/70">
                    <td className="py-1.5 pr-3 pl-3 text-sm font-medium text-gray-600">{group.label} 소계</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-sm text-gray-600">{group.subtotal.toLocaleString()}</td>
                    <td className="py-1.5 text-right font-mono text-sm text-gray-600">
                      {total > 0 ? (group.subtotal / total * 100).toFixed(1) : '0.0'}%
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              {/* 총합 */}
              <tr className="bg-gray-50 font-medium border-t-2">
                <td className="py-2 pr-3">합계</td>
                <td className="py-2 pr-3 text-right font-mono">{total.toLocaleString()}</td>
                <td className="py-2 text-right font-mono">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

// ==================== 일별 데이터 테이블 ====================

const DailyDataTable = ({ items }: { items: DailyAdDataItem[] }) => (
  <Card>
    <div className="p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">일별 광고 데이터</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-3">날짜</th>
              <th className="pb-2 pr-3 text-right">리워드 시청</th>
              <th className="pb-2 pr-3 text-right">배너 노출</th>
              <th className="pb-2 pr-3 text-right">배너 클릭</th>
              <th className="pb-2 text-right">배너 CTR</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const ctr = item.bannerImpressions > 0
                ? (item.bannerClicks / item.bannerImpressions * 100).toFixed(2)
                : '0.00';
              return (
                <tr key={item.date} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 pr-3 font-mono">{item.date}</td>
                  <td className="py-2 pr-3 text-right font-mono">{item.rewardWatch.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right font-mono">{item.bannerImpressions.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right font-mono">{item.bannerClicks.toLocaleString()}</td>
                  <td className="py-2 text-right font-mono">{ctr}%</td>
                </tr>
              );
            })}
            {/* 합계 행 */}
            <tr className="bg-gray-50 font-medium">
              <td className="py-2 pr-3">합계</td>
              <td className="py-2 pr-3 text-right font-mono">
                {items.reduce((s, i) => s + i.rewardWatch, 0).toLocaleString()}
              </td>
              <td className="py-2 pr-3 text-right font-mono">
                {items.reduce((s, i) => s + i.bannerImpressions, 0).toLocaleString()}
              </td>
              <td className="py-2 pr-3 text-right font-mono">
                {items.reduce((s, i) => s + i.bannerClicks, 0).toLocaleString()}
              </td>
              <td className="py-2 text-right font-mono text-gray-400">-</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

// ==================== 사용자 행동 요약 ====================

const UserBehaviorCard = ({ data }: { data: UserBehaviorSummary }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
    <Card>
      <div className="p-5">
        <p className="text-xs text-gray-500 mb-1">활성 유저</p>
        <p className="text-xl font-bold text-gray-900">{data.activeUsers.toLocaleString()}명</p>
      </div>
    </Card>
    <Card>
      <div className="p-5">
        <p className="text-xs text-gray-500 mb-1">투표 수</p>
        <p className="text-xl font-bold text-blue-600">{data.totalVotes.toLocaleString()}</p>
      </div>
    </Card>
    <Card>
      <div className="p-5">
        <p className="text-xs text-gray-500 mb-1">출석 수</p>
        <p className="text-xl font-bold text-green-600">{data.totalAttendance.toLocaleString()}</p>
      </div>
    </Card>
    <Card>
      <div className="p-5">
        <p className="text-xs text-gray-500 mb-1">1인당 평균 광고</p>
        <p className="text-xl font-bold text-purple-600">{data.adWatchPerUser}회</p>
      </div>
    </Card>
  </div>
);

// ==================== 배너 위치별 성과 ====================

const BannerPositionTable = ({ items }: { items: BannerPositionStats[] }) => (
  <Card>
    <div className="p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">배너 위치별 성과</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 pr-3">위치</th>
              <th className="pb-2 pr-3 text-right">노출 (PV)</th>
              <th className="pb-2 pr-3 text-right">클릭</th>
              <th className="pb-2 text-right">CTR</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.position} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 pr-3">{item.label}</td>
                <td className="py-2 pr-3 text-right font-mono">{item.pv.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right font-mono">{item.clicks.toLocaleString()}</td>
                <td className="py-2 text-right font-mono">{item.ctr}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </Card>
);

// ==================== 터치포인트별 일별 매트릭스 ====================

const MATRIX_COLORS = ['#3B82F6', '#22C55E', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// 매트릭스 컬럼을 그룹 순서로 재정렬하고 그룹 경계 인덱스 반환
const reorderMatrixColumns = (touchpoints: string[], touchpointLabels: string[]) => {
  const assignedLocations = new Set(TOUCHPOINT_GROUPS.flatMap(g => g.locations));
  const orderedIndices: number[] = [];
  const groupBoundaries: number[] = []; // 각 그룹 시작 인덱스 (첫 그룹 제외)

  for (const group of TOUCHPOINT_GROUPS) {
    const groupIndices = group.key === 'etc'
      ? touchpoints.map((tp, i) => ({ tp, i })).filter(({ tp }) => !assignedLocations.has(tp)).map(({ i }) => i)
      : touchpoints.map((tp, i) => ({ tp, i })).filter(({ tp }) => group.locations.includes(tp)).map(({ i }) => i);
    if (groupIndices.length > 0) {
      if (orderedIndices.length > 0) groupBoundaries.push(orderedIndices.length);
      orderedIndices.push(...groupIndices);
    }
  }

  // 매핑 안 된 것 추가 (안전장치)
  const orderedSet = new Set(orderedIndices);
  for (let i = 0; i < touchpoints.length; i++) {
    if (!orderedSet.has(i)) orderedIndices.push(i);
  }

  const boundarySet = new Set(groupBoundaries);
  return {
    orderedTouchpoints: orderedIndices.map(i => touchpoints[i]),
    orderedLabels: orderedIndices.map(i => touchpointLabels[i]),
    isGroupStart: orderedIndices.map((_, idx) => boundarySet.has(idx)),
  };
};

const TouchpointDailyMatrixSection = ({
  data, matrixFrom, matrixTo, setMatrixFrom, setMatrixTo,
}: {
  data: TouchpointDailyMatrixResponse;
  matrixFrom: string;
  matrixTo: string;
  setMatrixFrom: (v: string) => void;
  setMatrixTo: (v: string) => void;
}) => {
  const { orderedTouchpoints, orderedLabels, isGroupStart } = reorderMatrixColumns(data.touchpoints, data.touchpointLabels);

  // 컬럼별 합계
  const colTotals: Record<string, number> = {};
  let grandTotal = 0;
  for (const tp of orderedTouchpoints) colTotals[tp] = 0;
  for (const row of data.rows) {
    for (const tp of orderedTouchpoints) {
      colTotals[tp] += row.counts[tp] ?? 0;
    }
    grandTotal += row.total;
  }

  // 차트 데이터 (원본 순서 사용 — 차트는 그룹 구분 불필요)
  const chartData = data.rows.map(row => ({
    date: row.date.slice(5), // MM-DD
    ...data.touchpoints.reduce((acc, tp, i) => {
      acc[data.touchpointLabels[i]] = row.counts[tp] ?? 0;
      return acc;
    }, {} as Record<string, number>),
  }));

  const groupBorderClass = (idx: number) => isGroupStart[idx] ? 'border-l-2 border-l-gray-300 pl-2' : '';

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h3 className="text-sm font-semibold text-gray-700">터치포인트별 일별 광고 시청</h3>
            <div className="flex items-center gap-2">
              <input type="date" value={matrixFrom} onChange={e => setMatrixFrom(e.target.value)} className="h-7 px-2 text-xs border rounded bg-white" />
              <span className="text-xs text-gray-400">~</span>
              <input type="date" value={matrixTo} onChange={e => setMatrixTo(e.target.value)} className="h-7 px-2 text-xs border rounded bg-white" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-1.5 pr-2 sticky left-0 bg-white whitespace-nowrap">날짜</th>
                  {orderedLabels.map((label, i) => (
                    <th key={orderedTouchpoints[i]} className={`pb-1.5 px-1 text-right whitespace-nowrap ${groupBorderClass(i)}`}>{label}</th>
                  ))}
                  <th className="pb-1.5 text-right font-bold border-l-2 border-l-gray-300 pl-1.5">합계</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(row => (
                  <tr key={row.date} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-1.5 pr-2 font-mono whitespace-nowrap sticky left-0 bg-white">{row.date.slice(5)}</td>
                    {orderedTouchpoints.map((tp, i) => {
                      const count = row.counts[tp] ?? 0;
                      const pct = row.total > 0 ? (count / row.total * 100).toFixed(1) : '0.0';
                      return (
                        <td key={tp} className={`py-1.5 px-1 text-right font-mono whitespace-nowrap ${groupBorderClass(i)}`}>
                          {count === 0
                            ? <span className="text-gray-300">0</span>
                            : <>{count.toLocaleString()}<span className="text-gray-400 ml-0.5" style={{ fontSize: '10px' }}>({pct}%)</span></>}
                        </td>
                      );
                    })}
                    <td className="py-1.5 text-right font-mono font-medium whitespace-nowrap border-l-2 border-l-gray-300 pl-1.5">{row.total.toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="py-1.5 pr-2 sticky left-0 bg-gray-50">합계</td>
                  {orderedTouchpoints.map((tp, i) => {
                    const pct = grandTotal > 0 ? (colTotals[tp] / grandTotal * 100).toFixed(1) : '0.0';
                    return (
                      <td key={tp} className={`py-1.5 px-1 text-right font-mono whitespace-nowrap ${groupBorderClass(i)}`}>
                        {colTotals[tp].toLocaleString()}<span className="text-gray-400 ml-0.5" style={{ fontSize: '10px' }}>({pct}%)</span>
                      </td>
                    );
                  })}
                  <td className="py-1.5 text-right font-mono font-bold whitespace-nowrap border-l-2 border-l-gray-300 pl-1.5">{grandTotal.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* 추이 그래프 */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">터치포인트별 일별 추이</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '11px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '11px' }} width={40} />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {data.touchpointLabels.map((label, i) => (
                <Line key={label} type="monotone" dataKey={label} stroke={MATRIX_COLORS[i % MATRIX_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};
